import { create } from 'zustand';
import { User } from '@/types';

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  isOnboardingRequired: boolean;
  isSetupRequired: boolean;
  /** W1.2 — session created but still needs a TOTP/recovery challenge. */
  requiresTwoFactor: boolean;
  /** W1.2 — `security.enforce_2fa` is on and this user has no 2FA enrolled yet. */
  requiresTwoFactorEnrollment: boolean;
  error: string | null;

  setUser: (user: User | null) => void;
  setLoading: (loading: boolean) => void;
  setInitialized: (initialized: boolean) => void;
  setOnboardingRequired: (required: boolean) => void;
  setSetupRequired: (required: boolean) => void;
  setTwoFactorRequirement: (requiresTwoFactor: boolean, requiresTwoFactorEnrollment: boolean) => void;
  setError: (error: string | null) => void;
  reset: () => void;
}

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  isInitialized: false,
  isOnboardingRequired: false,
  isSetupRequired: false,
  requiresTwoFactor: false,
  requiresTwoFactorEnrollment: false,
  error: null,
};

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,

  setUser: (user) => {
    // Persist a minimal boolean breadcrumb so AuthInitializer can render
    // children optimistically on reload without a blocking full-screen spinner.
    // NO PII is stored — just a presence flag. Cleared on logout / reset.
    if (typeof window !== 'undefined') {
      if (user) {
        localStorage.setItem('admin_session_hint', '1');
      } else {
        localStorage.removeItem('admin_session_hint');
      }
    }
    set({
      user,
      isAuthenticated: !!user,
      error: null,
    });
  },

  setLoading: (isLoading) => set({ isLoading }),

  setInitialized: (isInitialized) => set({ isInitialized }),

  setOnboardingRequired: (isOnboardingRequired) => set({ isOnboardingRequired }),

  setSetupRequired: (isSetupRequired) => set({ isSetupRequired }),

  setTwoFactorRequirement: (requiresTwoFactor, requiresTwoFactorEnrollment) =>
    set({ requiresTwoFactor, requiresTwoFactorEnrollment }),

  setError: (error) => set({ error }),

  reset: () => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('admin_session_hint');
    }
    set(initialState);
  },
}));
